<template>
    <flex-container style="width: 24vw; max-width: 600px">
        <iframe
            ref="previewRef"
            title="Assistent"
            :src="assistentSource"
            style="border: none; height: 75dvh; min-height: 700px; width: 100%;"
            referrerpolicy="no-referrer"
        />
        <assistent-preview-footer :assistent-id="assistent.externalId" />
    </flex-container>
</template>

<script setup lang="ts">
    import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
    import type { Assistent, QuickOption } from '@keyvue/one-api-client';
    import AssistentPreviewFooter from '@/ai/assistent/components/forms/display/AssistentPreviewFooter.vue';

    interface Props {
        assistent: Assistent;
    }

    const props = defineProps<Props>();
    const isReady = ref(false);
    const previewRef = ref<HTMLIFrameElement>();

    const assistentSource = computed(() => {
        const queryParams = new URLSearchParams();
        queryParams.set('assistent', props.assistent.id.value);
        queryParams.set('preview', 'true');

        return [
            import.meta.env.VITE_WINCK_ASSISTENT_HOST,
            import.meta.env.VITE_WINCK_ASSISTENT_BASEPATH,
            `?${queryParams.toString()}`,
        ].filter((v) => !!v).join('');
    });

    function sendMessage(type: string, payload: Record<string, any> = {}) {
        previewRef.value?.contentWindow?.postMessage({
            type,
            payload,
        }, assistentSource.value);
    }

    async function convertAvatarToBase64(avatarUrl: string) {
        const response = await fetch(avatarUrl);
        const blob = await response.blob();

        return new Promise<string | null>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (typeof reader.result === 'string') {
                    resolve(reader.result);
                    return;
                }

                resolve(null);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    async function sendUpdatedConfiguration() {
        if (!isReady.value) {
            return;
        }

        const data = { ...props.assistent };

        // clear assistent from avatar (if exists)
        if (data.avatar?.assistent) {
            data.avatar.assistent = null;
        }

        // convert blob avatar to base64
        if (data.avatar?.url?.startsWith('blob:')) {
            data.avatar.url = await convertAvatarToBase64(data.avatar.url);
        }

        data.widget.quickOptions.forEach((question: QuickOption) => {
            // @todo timo mag dit fixen
            // @ts-ignore
            // eslint-disable-next-line no-param-reassign
            delete question.widget;
        });

        sendMessage('update-assistent', {
            assistent: JSON.stringify({
                ...data,
                id: data.id.value,
            }),
        });
    }

    async function messageHandler(event: MessageEvent) {
        if (!assistentSource.value.startsWith(event.origin)) {
            return;
        }

        const { type, payload } = event.data;
        if (!type || !payload) {
            return;
        }

        if (type === 'ready') {
            isReady.value = true;
            sendUpdatedConfiguration();
        }
    }

    watch(() => props.assistent, () => {
        sendUpdatedConfiguration();
    }, { deep: true });

    onMounted(() => {
        window.addEventListener('message', messageHandler);
    });

    onUnmounted(() => {
        window.removeEventListener('message', messageHandler);
    });
</script>
